import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { FileText, ClipboardCheck, Wrench, Truck, ArrowUpRight, Clock } from "lucide-react";

const statusMeta = {
  Received: { icon: FileText, color: "bg-blue-50 text-blue-700 border-blue-200" },
  Acknowledged: { icon: ClipboardCheck, color: "bg-amber-50 text-amber-700 border-amber-200" },
  "In Production": { icon: Wrench, color: "bg-violet-50 text-violet-700 border-violet-200" },
  Dispatched: { icon: Truck, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({ counts: {}, total_po: 0, total_value: 0, upcoming: [] }));
  }, []);

  if (!stats) {
    return <Layout title="Dashboard"><div className="text-sm text-gray-500">Loading...</div></Layout>;
  }

  return (
    <Layout
      title="Dashboard"
      subtitle="Overview of your purchase orders and production pipeline"
      actions={
        <Link to="/upload" data-testid="upload-cta" className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2">
          Upload PO <ArrowUpRight size={14} />
        </Link>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {Object.entries(statusMeta).map(([status, meta]) => {
          const Icon = meta.icon;
          return (
            <div key={status} data-testid={`stat-${status.replace(/\s+/g, "-").toLowerCase()}`} className="bg-white border border-gray-200 rounded-md p-5">
              <div className="flex items-center justify-between mb-4">
                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${meta.color}`}>
                  <Icon size={16} />
                </div>
                <span className="text-[10px] uppercase tracking-widest text-gray-400 font-mono">{status}</span>
              </div>
              <div className="font-display text-3xl font-semibold text-slate-900">{stats.counts?.[status] || 0}</div>
              <div className="text-xs text-gray-500 mt-1">purchase orders</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-base font-semibold text-slate-900">Upcoming due dates</h2>
              <p className="text-xs text-gray-500 mt-0.5">POs due in the next 14 days</p>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400">{stats.upcoming?.length || 0} items</span>
          </div>
          {(stats.upcoming || []).length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500" data-testid="upcoming-empty">
              <Clock className="mx-auto mb-2 text-gray-300" size={28} />
              No PO's coming due in the next 14 days.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {stats.upcoming.map((po) => (
                <Link key={po.id} to={`/orders/${po.id}`} className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2 rounded-md transition-colors">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{po.po_number || "—"}</div>
                    <div className="text-xs text-gray-500">{po.customer_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-900 font-mono">{po.due_date || "—"}</div>
                    <div className={`text-[11px] mt-0.5 font-medium ${po.days_left < 0 ? "text-red-600" : po.days_left <= 3 ? "text-amber-600" : "text-gray-500"}`}>
                      {po.days_left < 0 ? `Overdue by ${Math.abs(po.days_left)}d` : `${po.days_left}d left`}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-6">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-4">At a glance</h2>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-gray-500 font-mono uppercase tracking-widest">Total POs</div>
              <div className="font-display text-2xl font-semibold text-slate-900 mt-1" data-testid="total-po">{stats.total_po}</div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-500 font-mono uppercase tracking-widest">Total PO value</div>
              <div className="font-display text-2xl font-semibold text-slate-900 mt-1">
                ₹ {Number(stats.total_value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
